import React, { useMemo } from 'react'
import { Divider, Grid } from '@fs/zion-ui'
import HouseholdDisplay from './components/HouseholdDisplay'
import FloatingPersonsList from './components/FloatingPersonsList'
import LoadingList from './components/LoadingList'
import FilterAndActions from './components/FilterAndActions'
import NewGroupCard from './components/NewGroupCard'

/**
 * @typedef {import('./hooks/useHouseholdsList').HouseholdListProps} PersonsListProps
 */

/**
 * @typedef {Object} Props
 * @property {(person: any) => void} onPersonSelect
 * @property {string} [description]
 * @property {string} filterValue
 * @property {(value: string) => void} setFilterValue
 */

/**
 * Renders a list of persons, grouped by household, and floating persons.
 *
 * NOTE: This component is designed to be used in conjunction with the `useHouseholdsList` hook
 * and a filter component (e.g., `FilterInput`) to manage state and filtering logic.
 *
 * @param {PersonsListProps & Props} props
 * @returns {JSX.Element}
 */
const HouseholdList = ({
  filteredHouseholdsWithPersons = [],
  filteredFloatingPersons = [],
  onPersonSelect,
  showLoading = false,
  isJiapu = false,
  description,
  filterValue,
  setFilterValue,
}) => {
  // Filter out households with no persons to avoid rendering empty lists
  const cleanedHouseholdsWithPersons = useMemo(
    () => filteredHouseholdsWithPersons.filter(({ persons }) => persons && persons.length > 0),
    [filteredHouseholdsWithPersons]
  )

  // Early return loading skeletons if data is still loading
  if (showLoading) return <LoadingList />

  return (
    <>
      {/* Description and Filter */}
      <FilterAndActions description={description} filterValue={filterValue} onFilterChange={setFilterValue} />

      {/* Person List */}
      {!isJiapu && (
        <Grid guttersY="none">
          {/* List of Households with Persons */}
          {cleanedHouseholdsWithPersons.map(({ household, persons }) => (
            <HouseholdDisplay
              key={household?.id}
              household={household}
              persons={persons}
              onPersonSelect={onPersonSelect}
            />
          ))}

          {cleanedHouseholdsWithPersons.length > 0 && <Divider marginY="xxs" fullWidth />}

          {/* Floating Persons (not attached to households) */}
          <FloatingPersonsList persons={filteredFloatingPersons} onPersonSelect={onPersonSelect} />
        </Grid>
      )}

      {/* New Group Card at the end of the list */}
      <NewGroupCard />
    </>
  )
}

export default HouseholdList
